import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VideoTutorials = () => {
  const { isDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const categories = [
  { value: 'all', label: 'All Tutorials' },
  { value: 'getting-started', label: 'Getting Started' },
  { value: 'form-builder', label: 'Form Builder' },
  { value: 'advanced', label: 'Advanced Features' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'tips', label: 'Tips & Tricks' }];


  const videos = [
  {
    id: 1,
    title: 'Getting Started: Your First Form',
    category: 'getting-started',
    duration: '5:30',
    views: 12500,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_16ab31c37-1768666762811.png",
    description: 'Learn how to create your first form from scratch'
  },
  {
    id: 2,
    title: 'Drag & Drop Form Builder Tutorial',
    category: 'form-builder',
    duration: '8:45',
    views: 9800,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_141710715-1767182530762.png",
    description: 'Master the drag-and-drop interface'
  },
  {
    id: 3,
    title: 'Setting Up Payment Integration',
    category: 'integrations',
    duration: '12:20',
    views: 7600,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a3eb1eee-1766474568663.png",
    description: 'Configure Stripe and PayPal payments'
  },
  {
    id: 4,
    title: 'Advanced Conditional Logic',
    category: 'advanced',
    duration: '15:10',
    views: 5400,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_193326c61-1765315797297.png",
    description: 'Create dynamic forms with conditional fields'
  },
  {
    id: 6,
    title: 'Optimizing Form Conversion Rates',
    category: 'tips',
    duration: '9:15',
    views: 8900,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_106bd491c-1765118303836.png",
    description: 'Best practices for higher conversions'
  }];


  const filteredVideos = videos?.filter((video) => {
    const matchesSearch = searchQuery === '' ||
    video?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    video?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || video?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            
            <Input
              type="search"
              placeholder="Search video tutorials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10" />
            
          </div>
        </div>
        <div className="w-full md:w-64">
          <Select
            options={categories}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder="Filter by category" />
          
        </div>
      </div>

      {/* Videos Grid */}
      {filteredVideos?.length === 0 ?
      <div className={`p-12 rounded-lg border text-center ${
      isDarkMode ?
      'bg-[#0f1410] border-[#1a2820]' :
      'bg-gray-50 border-gray-200'}`
      }>
          <Icon name="Video" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No videos found
          </h3>
          <p className="text-sm text-muted-foreground">
            Try adjusting your search or filter criteria
          </p>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos?.map((video) =>
        <div
          key={video?.id}
          onClick={() => setSelectedVideo(video)}
          className={`rounded-lg border overflow-hidden transition-all cursor-pointer ${
          isDarkMode ?
          'bg-[#0f1410] border-[#1a2820] hover:border-[#10b981]/50' :
          'bg-white border-gray-200 hover:border-[#107570]/50 hover:shadow-lg'}`
          }>
          
              {/* Thumbnail */}
              <div className="relative aspect-video bg-muted">
                <img
              src={video?.thumbnail}
              alt={video?.title}
              className="w-full h-full object-cover" />
            
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isDarkMode ? 'bg-[#10b981]' : 'bg-[#107570]'}`
              }>
                    <Icon name="Play" size={28} className="text-white ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-xs text-white">
                  {video?.duration}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
              isDarkMode ?
              'bg-[#10b981]/10 text-[#10b981]' :
              'bg-[#107570]/10 text-[#107570]'}`
              }>
                    {categories?.find((c) => c?.value === video?.category)?.label}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2">
                  {video?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {video?.description}
                </p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="Eye" size={14} />
                  {video?.views?.toLocaleString()} views
                </div>
              </div>
            </div>
        )}
        </div>
      }

      {/* Video Player Modal */}
      {selectedVideo &&
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-4xl rounded-lg overflow-hidden ${
        isDarkMode ? 'bg-[#0a0f0d]' : 'bg-white'}`
        }>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${
          isDarkMode ? 'border-[#1a2820]' : 'border-gray-200'}`
          }>
              <h3 className="text-lg font-semibold text-foreground">
                {selectedVideo?.title}
              </h3>
              <button
              onClick={() => setSelectedVideo(null)}
              className="p-2 hover:bg-accent rounded-lg transition-colors">
              
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Video Player Placeholder */}
            <div className="aspect-video bg-black flex items-center justify-center">
              <div className="text-center text-white">
                <Icon name="Video" size={64} className="mx-auto mb-4 opacity-50" />
                <p className="text-sm opacity-75">Video player would load here</p>
                <p className="text-xs opacity-50 mt-2">Duration: {selectedVideo?.duration}</p>
              </div>
            </div>

            {/* Description */}
            <div className="p-4">
              <p className="text-sm text-muted-foreground">
                {selectedVideo?.description}
              </p>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default VideoTutorials;